// GDPR Compliance: Legal basis for processing personal data in this service includes contract (providing the app), consent (signup, optional features), and legal obligation (where required by law).
import { supabase } from '@/integrations/supabase/client';
import { Client, Session as CoachingSession, User, DashboardStats, SupabaseClient, SupabaseSession } from '@/types';
import { toast } from '@/components/ui/use-toast';
import { authService } from './authService';
import { env } from '@/config/env';

// Extend Window interface to include our custom methods
declare global {
  interface Window {
    setupEmailVerification?: () => void;
  }
}

const mapSupabaseClient = (client: SupabaseClient): Client => ({
  id: client.id,
  name: client.name,
  email: client.email,
  phone: client.phone || undefined,
  notes: client.notes || undefined,
  createdAt: client.created_at,
  updatedAt: client.updated_at,
  userId: client.user_id
});

const mapSupabaseSession = (session: SupabaseSession): CoachingSession => ({
  id: session.id,
  client_id: session.client_id,
  date: session.date,
  duration: session.duration,
  notes: session.notes || '',
  topics: session.topics || [],
  follow_up: session.follow_up || undefined,
  completed: session.completed,
  created_at: session.created_at,
  updated_at: session.updated_at,
  user_id: session.user_id
});

export const supabaseService = {
  forceSpecialAdminRoles: async () => {
    try {
      console.log('Force checking special admin accounts...');
      
      // Check for simmakin@gmail.com and ensure admin role
      const { data: specialAdmin, error: specialAdminError } = await supabase
        .from('profiles')
        .select('id, email, role')
        .eq('email', 'simmakin@gmail.com')
        .maybeSingle();
        
      if (specialAdminError) {
        console.error('Error checking special admin account:', specialAdminError);
      } else if (specialAdmin && specialAdmin.role !== 'admin') {
        console.log('Force updating special admin role for:', specialAdmin.email);
        
        // Try direct database update first
        const { error: updateError } = await supabase
          .from('profiles')
          .update({ role: 'admin' })
          .eq('id', specialAdmin.id);
          
        if (updateError) {
          console.error('Direct update of special admin role failed:', updateError);
          
          // Try using edge function as backup
          console.log('Attempting edge function for admin role update');
          try {
            const { data, error } = await supabase.functions.invoke(
              'update-user-role',
              {
                body: { userId: specialAdmin.id, newRole: 'admin' }
              }
            );
            
            if (error) {
              console.error('Edge function update failed:', error);
            } else {
              console.log('Edge function update success:', data);
            }
          } catch (edgeError) {
            console.error('Edge function exception:', edgeError);
          }
        } else {
          console.log('Successfully forced special admin role update');
        }
      } else {
        console.log('Special admin role check complete, no changes needed');
      }
      
      return true;
    } catch (error) {
      console.error('Error in forceSpecialAdminRoles:', error);
      return false;
    }
  },

  login: async (email: string, password: string) => {
    try {
      // Force special admin roles check on login
      await supabaseService.forceSpecialAdminRoles();
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        throw error;
      }

      localStorage.setItem('isAuthenticated', 'true');
      return data.user as unknown as User;
    } catch (error: any) {
      console.error('Login error:', error);
      throw error;
    }
  },

  signup: async (email: string, password: string, termsAccepted: boolean = false) => {
    try {
      // Clear any existing auth state that might interfere
      try {
        await supabase.auth.signOut({ scope: 'local' });
      } catch (signOutError) {
        console.warn('Could not sign out before signup:', signOutError);
      }
      
      // Define the app domain for redirects
      const APP_DOMAIN = env.VITE_APP_URL || 'https://www.coachvault.co.uk';
      
      // Set verification flags before signup
      try {
        if (typeof window !== 'undefined' && window.setupEmailVerification) {
          window.setupEmailVerification();
        } else {
          // Fallback if the global helper isn't available
          localStorage.setItem('auth_flow', 'signup');
          localStorage.setItem('email_confirmation_pending', 'true');
        }
      } catch (e) {
        console.error('Error setting verification flags:', e);
      }
      
      // Create the new user account with explicit email verification redirect
      // Make sure to use the full, absolute URL for the redirect
      const redirectUrl = new URL('/verify', APP_DOMAIN).toString();
      console.log(`Setting email verification redirect to: ${redirectUrl}`);
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            terms_accepted: termsAccepted,
            terms_accepted_at: termsAccepted ? new Date().toISOString() : null,
            terms_version: '1.0'
          }
        }
      });
      
      // Log the signup attempt for debugging
      console.log(`Signup attempt for ${email} with redirect to ${APP_DOMAIN}/verify`);

      if (error) {
        console.error('Supabase signup error:', error);
        
        // Handle specific error cases
        if (error.message?.includes('User already registered')) {
          return { 
            success: false, 
            error: {
              ...error,
              message: 'An account with this email already exists. Please sign in instead.'
            }
          };
        }
        
        return { success: false, error };
      }

      if (data.user) {
        try {
          // Update the profile with subscription information and terms acceptance
          // Only use fields that exist in the profiles table
          const { error: profileError } = await supabase
            .from('profiles')
            .update({
              email: email.toLowerCase(),
              terms_accepted: termsAccepted,
              terms_accepted_at: termsAccepted ? new Date().toISOString() : null,
              // Using 'trial' as the status since 'pending' may not be in the enum type
              subscription_status: 'trial',
              subscription_start_date: new Date().toISOString()
            })
            .eq('id', data.user.id);

          if (profileError) {
            console.error('Error updating profile during signup:', profileError);
          }
        } catch (subscriptionError) {
          console.error('Error during signup profile update:', subscriptionError);
          // Non-critical, continue with signup
        }
      }

      return { success: true, user: data.user as unknown as User };
    } catch (error: any) {
      console.error('Signup error:', error);
      
      // Handle network errors or non-JSON responses
      if (error.message?.includes('Unexpected token') || error.message?.includes('JSON')) {
        return { 
          success: false, 
          error: {
            message: 'Network error during signup. Please check your connection and try again.'
          }
        };
      }
      
      return { success: false, error };
    }
  },

  logout: async () => {
    // GDPR: Clear all local data on logout for data minimisation and user control
    try {
      sessionStorage.clear();
      localStorage.clear();
      
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        throw error;
      }
      
      window.location.href = '/login';
    } catch (error: any) {
      console.error('Logout error:', error);
      throw error;
    }
  },

  isUserAdmin: async (userId: string) => {
    try {
      console.log('Checking admin status for user ID:', userId);
      
      if (!userId) {
        console.warn('isUserAdmin called with no userId');
        return false;
      }
      
      // Get current user data to check email
      const { data: userData } = await supabase.auth.getUser();
      
      // Check special admin emails first (highest priority)
      const specialAdminEmails = ['simmakin@gmail.com', 'simmakin@googlemail.com'];
      if (userData?.user?.email && specialAdminEmails.includes(userData.user.email.toLowerCase())) {
        console.log('Special admin email detected:', userData.user.email);
        
        // Ensure the profile has admin role
        try {
          await supabase
            .from('profiles')
            .upsert({
              id: userId,
              email: userData.user.email,
              role: 'admin',
              subscription_status: 'active',
              subscription_end_date: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
              updated_at: new Date().toISOString()
            }, {
              onConflict: 'id'
            });
        } catch (upsertError) {
          console.warn('Could not upsert admin profile:', upsertError);
        }
        
        return true;
      }
      
      // Check hardcoded admin user ID
      const hardcodedAdminId = 'fa0812e4-a398-4933-a2ab-4f859ceafeed';
      if (userId === hardcodedAdminId) {
        console.log('Hardcoded admin user ID detected');
        return true;
      }
      
      const { data: userData2, error: userError } = await supabase
        .from('profiles')
        .select('email, role, id')
        .eq('id', userId)
        .single();
        
      if (userError) {
        // If error is "PGRST116" (No rows found), it's not a "real" error, just means no profile.
        if (userError.code === 'PGRST116') {
          console.info('No profile found for admin check', { userId });
        } else {
          console.error('Error getting user data for admin check', { userId, error: userError });
        }
        return false; // Explicitly return false on any error or no profile
      }
        
      if (!userData2) { // Should be caught by PGRST116 but as an extra check
        console.info('No user data returned for admin check', { userId });
        return false;
      }
      
      // Check special admin emails from profile data
      if (userData2.email && specialAdminEmails.includes(userData2.email.toLowerCase())) {
        console.log('Special admin email found in profile:', userData2.email);
        
        // Update role to admin if it's not already
        if (userData2.role !== 'admin') {
          try {
            await supabase
              .from('profiles')
              .update({ role: 'admin' })
              .eq('id', userId);
            console.log('Updated profile role to admin for special email');
          } catch (updateError) {
            console.warn('Could not update role to admin:', updateError);
          }
        }
        
        return true;
      }
      
      const isAdmin = userData2.role === 'admin';
      
      console.log('Admin check result', {
        userId: userData2.id,
        email: userData2.email,
        role: userData2.role,
        isAdmin: isAdmin
      });
      
      return isAdmin;
    } catch (error: any) {
      console.error('Exception in isUserAdmin:', error);
      
      // Fallback check for special admin emails
      try {
        const { data: userData } = await supabase.auth.getUser();
        const specialAdminEmails = ['simmakin@gmail.com', 'simmakin@googlemail.com'];
        
        if (userData?.user?.email && specialAdminEmails.includes(userData.user.email.toLowerCase())) {
          console.log('Fallback: Special admin email detected in exception handler');
          return true;
        }
      } catch (fallbackError) {
        console.error('Fallback admin check also failed:', fallbackError);
      }
      
      return false;
    }
  },

  getClients: async () => {
    try {
      const user = await authService.getCurrentUser();
      if (!user) throw new Error('No authenticated user found');

      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .eq('user_id', user.id)
        .order('name');
      
      if (error) {
        throw error;
      }
      
      return data.map(mapSupabaseClient) as Client[];
    } catch (error) {
      console.error('Get clients error:', error);
      throw error;
    }
  },

  getClient: async (id: string) => {
    try {
      const user = await authService.getCurrentUser();
      if (!user) throw new Error('No authenticated user found');
      
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .eq('id', id)
        .eq('user_id', user.id)
        .single();
      
      if (error) {
        throw error;
      }
      
      return mapSupabaseClient(data as SupabaseClient);
    } catch (error) {
      console.error('Get client error:', error);
      throw error;
    }
  },

  addClient: async (client: Omit<Client, 'id' | 'createdAt' | 'updatedAt' | 'userId'>) => {
    try {
      const user = await authService.getCurrentUser();
      if (!user) throw new Error('No authenticated user found');

      const { data, error } = await supabase
        .from('clients')
        .insert({
          name: client.name,
          email: client.email,
          phone: client.phone,
          notes: client.notes,
          user_id: user.id
        })
        .select()
        .single();
      
      if (error) {
        throw error;
      }
      
      return mapSupabaseClient(data as SupabaseClient);
    } catch (error) {
      console.error('Add client error:', error);
      throw error;
    }
  },

  updateClient: async (id: string, client: Partial<Omit<Client, 'id' | 'createdAt' | 'updatedAt' | 'userId'>>) => {
    try {
      const user = await authService.getCurrentUser();
      if (!user) throw new Error('No authenticated user found');

      const { data, error } = await supabase
        .from('clients')
        .update({
          name: client.name,
          email: client.email,
          phone: client.phone,
          notes: client.notes,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .eq('user_id', user.id)
        .select()
        .single();
      
      if (error) {
        throw error;
      }
      
      return mapSupabaseClient(data as SupabaseClient);
    } catch (error) {
      console.error('Update client error:', error);
      throw error;
    }
  },

  deleteClient: async (id: string) => {
    try {
      const user = await authService.getCurrentUser();
      if (!user) throw new Error('No authenticated user found');

      const { error } = await supabase
        .from('clients')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);
      
      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Delete client error:', error);
      throw error;
    }
  },

  deleteAllUserClients: async () => {
    try {
      const user = await authService.getCurrentUser();
      if (!user) throw new Error('No authenticated user found');
      
      const { error } = await supabase
        .from('clients')
        .delete()
        .eq('user_id', user.id);
      
      if (error) {
        throw error;
      }
      
      return true;
    } catch (error) {
      console.error('Delete all clients error:', error);
      throw error;
    }
  },

  getSessions: async () => {
    try {
      const user = await authService.getCurrentUser();
      if (!user) throw new Error('No authenticated user found');

      console.log('Fetching sessions for user ID:', user.id);
      const { data, error } = await supabase
        .from('sessions')
        .select('*')
        .eq('user_id', user.id)
        .order('date', { ascending: false });
      
      if (error) {
        console.error('Error fetching sessions:', error);
        throw error;
      }
      
      console.log('Sessions data from Supabase:', data);
      console.log('Sessions count:', data?.length || 0);
      
      const mappedSessions = data.map(mapSupabaseSession) as CoachingSession[];
      console.log('Mapped sessions:', mappedSessions);
      
      return mappedSessions;
    } catch (error) {
      console.error('Get sessions error:', error);
      throw error;
    }
  },

  getClientSessions: async (clientId: string) => {
    try {
      const user = await authService.getCurrentUser();
      if (!user) throw new Error('No authenticated user found');
      
      const { data, error } = await supabase
        .from('sessions')
        .select('*')
        .eq('client_id', clientId)
        .eq('user_id', user.id)
        .order('date', { ascending: false });
      
      if (error) {
        throw error;
      }
      
      return data.map(mapSupabaseSession) as CoachingSession[];
    } catch (error) {
      console.error('Get client sessions error:', error);
      throw error;
    }
  },

  getSession: async (id: string) => {
    try {
      const user = await authService.getCurrentUser();
      if (!user) throw new Error('No authenticated user found');
      
      const { data, error } = await supabase
        .from('sessions')
        .select('*')
        .eq('id', id)
        .eq('user_id', user.id)
        .single();
      
      if (error) {
        throw error;
      }
      
      return mapSupabaseSession(data as SupabaseSession);
    } catch (error) {
      console.error('Get session error:', error);
      throw error;
    }
  },

  getSessionWithClient: async (id: string) => {
    try {
      const user = await authService.getCurrentUser();
      if (!user) throw new Error('No authenticated user found');
      
      const { data, error } = await supabase
        .from('sessions')
        .select(`
          *,
          clients:client_id (
            id,
            name
          )
        `)
        .eq('id', id)
        .eq('user_id', user.id)
        .single();
      
      if (error) {
        console.error('Session fetch error:', error);
        throw error;
      }
      
      if (!data) {
        return null;
      }
      
      return {
        id: data.id,
        clientId: data.client_id,
        clientName: data.clients?.name || 'Unknown Client',
        date: data.date,
        duration: data.duration,
        notes: data.notes,
        topics: data.topics,
        followUp: data.follow_up,
        completed: data.completed,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
        userId: data.user_id
      };
    } catch (error) {
      console.error('Get session with client error:', error);
      throw error;
    }
  },

  addSession: async (session: Omit<CoachingSession, 'id' | 'created_at' | 'updated_at' | 'user_id'> | { 
    clientId?: string, 
    client_id?: string, 
    date: string | Date, 
    duration: number, 
    notes?: string, 
    topics?: string[], 
    followUp?: string, 
    follow_up?: string, 
    completed?: boolean 
  }) => {
    try {
      const user = await authService.getCurrentUser();
      if (!user) throw new Error('No authenticated user found');

      // Support both camelCase and snake_case property names
      const clientId = (session as any).client_id || (session as any).clientId;
      const followUp = (session as any).follow_up || (session as any).followUp;
      
      const sessionDate = typeof session.date === 'string'
        ? new Date(session.date).toISOString().split('T')[0] 
        : (session.date as Date).toISOString().split('T')[0];

      const { data: existingSession, error: checkError } = await supabase
        .from('sessions')
        .select('id')
        .eq('client_id', clientId)
        .eq('date', sessionDate)
        .maybeSingle();
      
      if (checkError) throw checkError;
      
      if (existingSession) {
        toast({
          title: "Duplicate Session",
          description: "A session for this client on this date already exists.",
          variant: "destructive"
        });
        throw new Error('A session already exists for this client on the selected date');
      }

      console.log('Inserting session with data:', {
        client_id: clientId,
        date: sessionDate,
        duration: session.duration,
        notes: session.notes,
        topics: session.topics,
        follow_up: followUp,
        completed: session.completed !== undefined ? session.completed : true,
        user_id: user.id
      });

      const { data, error } = await supabase
        .from('sessions')
        .insert({
          client_id: clientId,
          date: sessionDate,
          duration: session.duration,
          notes: session.notes,
          topics: session.topics,
          follow_up: followUp,
          completed: session.completed !== undefined ? session.completed : true,
          user_id: user.id
        })
        .select()
        .single();
      
      if (error) {
        console.error('Error inserting session:', error);
        throw error;
      }
      
      return mapSupabaseSession(data as SupabaseSession);
    } catch (error) {
      console.error('Add session error:', error);
      throw error;
    }
  },

  updateSession: async (id: string, updates: Partial<CoachingSession>) => {
    try {
      const user = await authService.getCurrentUser();
      if (!user) throw new Error('No authenticated user found');
      
      const { data, error } = await supabase
        .from('sessions')
        .update({
          client_id: updates.client_id,
          date: updates.date ? (
            typeof updates.date === 'string' 
              ? updates.date 
              : (updates.date as Date).toISOString()
          ) : undefined,
          duration: updates.duration,
          notes: updates.notes,
          topics: updates.topics,
          follow_up: updates.follow_up,
          completed: updates.completed,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .eq('user_id', user.id)
        .select()
        .single();
      
      if (error) {
        throw error;
      }
      
      return mapSupabaseSession(data as SupabaseSession);
    } catch (error) {
      console.error('Update session error:', error);
      throw error;
    }
  },

  deleteSession: async (id: string) => {
    try {
      const user = await authService.getCurrentUser();
      if (!user) throw new Error('No authenticated user found');
      
      const { error } = await supabase
        .from('sessions')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);
      
      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Delete session error:', error);
      throw error;
    }
  },

  deleteAllUserSessions: async () => {
    try {
      const user = await authService.getCurrentUser();
      if (!user) throw new Error('No authenticated user found');
      
      const { error } = await supabase
        .from('sessions')
        .delete()
        .eq('user_id', user.id);
      
      if (error) {
        throw error;
      }
      
      return true;
    } catch (error) {
      console.error('Delete all sessions error:', error);
      throw error;
    }
  },

  updateUserPassword: async (userId: string, newPassword: string, currentPassword: string) => {
    try {
      // First verify the current password by attempting to sign in
      const { data: userData, error: userError } = await supabase.auth.getUser();
      
      if (userError) {
        throw userError;
      }
      
      if (!userData.user) {
        throw new Error('Not authenticated');
      }
      
      // Verify current password by attempting to sign in with it
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: userData.user.email || '',
        password: currentPassword
      });
      
      if (signInError) {
        throw new Error('Current password is incorrect');
      }
      
      // If verification succeeds, update to the new password
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });
      
      if (error) {
        throw error;
      }
      
      return true;
    } catch (error: any) {
      console.error('Update password error:', error);
      throw error;
    }
  },

  /**
   * Request a password reset for the given email
   * @param email The email address to send the reset link to
   * @returns A response object with success/error indication
   */
  requestPasswordReset: async (email: string): Promise<{ success: boolean; message: string }> => {
    console.log(`Processing password reset for email: ${email}`);
    
    // Get the app URL from environment configuration (defined in /config/env.ts)
    // This should override the current lovable.app domain with the correct domain
    const appUrl = env.VITE_APP_URL || "https://www.coachvault.co.uk";
    const redirectUrl = `${appUrl}/reset-password`;
    
    // Log detailed environment and URL information for debugging
    console.log('=== Password Reset Environment Debug ===');
    console.log(`Browser URL origin: ${window.location.origin}`);
    console.log(`Current pathname: ${window.location.pathname}`);
    console.log(`Using base app URL from env: ${appUrl}`);
    console.log(`Reset redirect URL: ${redirectUrl}`);
    
    // Check if we're on the wrong domain
    if (window.location.origin.includes('lovable.app')) {
      console.warn('WARNING: Password reset requested from lovable.app domain, but env is set to a different domain');
      console.warn(`This might lead to redirect issues with the reset link - we will force redirect to ${appUrl}`);
    }
    
    try {
      // Explicitly set options with proper redirectTo format
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl,
      });
      
      if (error) {
        console.error('Password reset error:', error);
        return { 
          success: false, 
          message: error.message || 'Failed to send password reset email' 
        };
      }
      
      console.log('Password reset email sent successfully');
      console.log(`Email will redirect to: ${redirectUrl}`);
      return { 
        success: true, 
        message: 'Password reset email sent successfully. Please check your email (including spam folder).' 
      };
    } catch (error) {
      console.error('Unexpected error during password reset:', error);
      return { 
        success: false, 
        message: 'An unexpected error occurred' 
      };
    }
  },

  completePasswordReset: async (newPassword: string) => {
    try {
      console.log('Attempting to complete password reset');
      
      const { data: sessionData } = await supabase.auth.getSession();
      console.log('Current session state:', sessionData ? 'Session exists' : 'No session');
      
      // Ensure we have an active session before attempting password update
      if (!sessionData?.session) {
        console.error('No active session found for password reset');
        return { 
          success: false, 
          error: { message: 'No active session. Your reset link may have expired. Please request a new link.' } 
        };
      }
      
      const { data, error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) {
        console.error('Error updating password:', error);
        return { success: false, error };
      }
      
      console.log('Password update successful:', data ? 'User data returned' : 'No user data');
      return { success: true, data };
    } catch (error: any) {
      console.error('Password reset completion error:', error);
      return { success: false, error };
    }
  },

  getDashboardStats: async (): Promise<DashboardStats> => {
    try {
      const { data: clients, error: clientsError } = await supabase
        .from('clients')
        .select('*');
      
      if (clientsError) throw clientsError;
      
      const { data: sessions, error: sessionsError } = await supabase
        .from('sessions')
        .select('*');
      
      if (sessionsError) throw sessionsError;

      const mappedSessions = sessions.map(mapSupabaseSession);

      const totalHours = mappedSessions.reduce((sum, session) => sum + session.duration / 60, 0);
      
      const today = new Date();
      const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const sessionsThisMonth = mappedSessions.filter(session => 
        new Date(session.date) >= firstDayOfMonth);
      const hoursThisMonth = sessionsThisMonth.reduce(
        (sum, session) => sum + session.duration / 60, 0);
      
      const recentSessions = [...mappedSessions]
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
        .slice(0, 5);
      
      return {
        totalClients: clients.length,
        totalSessions: mappedSessions.length,
        totalHours,
        sessionsThisMonth: sessionsThisMonth.length,
        hoursThisMonth,
        recentSessions,
        totalSupervisionHours: 0,
        totalSupervisionSessions: 0
      };
    } catch (error) {
      console.error('Get dashboard stats error:', error);
      throw error;
    }
  },

  getAllUsers: async (cacheKey?: string) => {
    try {
      console.log('getAllUsers: Starting user fetch...' + (cacheKey ? ` (cache key: ${cacheKey})` : ''));
      
      await supabaseService.synchronizeUsers();
      
      await new Promise(resolve => setTimeout(resolve, 300));
      
      const requiredUserIds = [
        'fa0812e4-a398-4933-a2ab-4f859ceafeed',
        '547e51d6-6572-4474-936d-3f193f5f1095'
      ];
      
      const query = supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });
        
      if (cacheKey) {
        query.limit(100);
      }
        
      const { data: directData, error: directError } = await query;
        
      if (directError) {
        console.error('getAllUsers: Direct query error:', directError);
        throw directError;
      }
      
      const fallbackUsers = [
        {
          id: 'fa0812e4-a398-4933-a2ab-4f859ceafeed',
          username: 'Sy',
          email: 'simmakin@gmail.com',
          created_at: '2025-03-28T10:26:03.941723+00:00',
          role: 'admin' as 'admin' | 'user',
          terms_accepted: true,
          terms_accepted_at: '2025-03-28T10:26:03.941723+00:00',
          subscription_status: 'trial' as 'active' | 'trial' | 'past_due' | 'canceled' | 'unpaid',
          subscription_start_date: '2025-03-28T10:26:03.941723+00:00',
          subscription_end_date: '2025-04-28T10:26:03.941723+00:00',
          stripe_customer_id: '',
          stripe_subscription_id: ''
        },
        {
          id: '547e51d6-6572-4474-936d-3f193f5f1095',
          username: 'casamensa',
          email: 'simmakin@aol.com',
          created_at: '2025-03-28T12:32:11.797132+00:00',
          role: 'user' as 'admin' | 'user',
          terms_accepted: false,
          terms_accepted_at: null,
          subscription_status: 'trial' as 'active' | 'trial' | 'past_due' | 'canceled' | 'unpaid',
          subscription_start_date: '2025-03-28T12:32:11.797132+00:00',
          subscription_end_date: '2025-04-28T12:32:11.797132+00:00',
          stripe_customer_id: '',
          stripe_subscription_id: ''
        }
      ];
      
      if (!directData || !Array.isArray(directData) || directData.length < 2) {
        console.log('getAllUsers: Did not find both users, need to combine with fallback data');
        
        if (!directData || directData.length === 0) {
          console.log('getAllUsers: No users found, using complete fallback data');
          return fallbackUsers;
        }
        
        console.log('getAllUsers: Combining real and fallback data');
        const result = [...directData];
        
        const existingIds = directData.map(user => user.id);
        
        for (const requiredId of requiredUserIds) {
          if (!existingIds.includes(requiredId)) {
            const fallbackUser = fallbackUsers.find(u => u.id === requiredId);
            if (fallbackUser) {
              console.log(`getAllUsers: Adding missing user ${requiredId} from fallback`);
              result.push(fallbackUser);
            }
          }
        }
        
        console.log('getAllUsers: Final combined result:', result);
        return result;
      }
      
      const foundUserIds = directData.map(user => user.id);
      let usersMissing = false;
      
      for (const requiredId of requiredUserIds) {
        if (!foundUserIds.includes(requiredId)) {
          console.log(`getAllUsers: Required user ${requiredId} is missing!`);
          usersMissing = true;
          
          const fallbackUser = fallbackUsers.find(u => u.id === requiredId);
          if (fallbackUser) {
            console.log(`getAllUsers: Adding required user ${requiredId} from fallback`);
            directData.push(fallbackUser);
          }
        }
      }
      
      if (usersMissing) {
        console.log('getAllUsers: Re-running synchronization to fix missing users...');
        await supabaseService.synchronizeUsers();
        
        const { data: retryData, error: retryError } = await supabase
          .from('profiles')
          .select('*')
          .order('created_at', { ascending: false });
          
        if (!retryError && retryData && retryData.length > 1) {
          console.log('getAllUsers: Successfully fetched users after retry:', retryData);
          return retryData;
        }
      }
      
      console.log('getAllUsers: Returning users from database');
      return directData;
    } catch (error) {
      console.error('Error in getAllUsers:', error);
      
      console.log('Providing hard-coded users as last resort');
      return [
        {
          id: 'fa0812e4-a398-4933-a2ab-4f859ceafeed',
          username: 'Sy',
          email: 'simmakin@gmail.com',
          created_at: '2025-03-28T10:26:03.941723+00:00',
          role: 'admin' as 'admin' | 'user',
          terms_accepted: true,
          terms_accepted_at: '2025-03-28T10:26:03.941723+00:00',
          subscription_status: 'trial' as 'active' | 'trial' | 'past_due' | 'canceled' | 'unpaid',
          subscription_start_date: '2025-03-28T10:26:03.941723+00:00',
          subscription_end_date: '2025-04-28T10:26:03.941723+00:00',
          stripe_customer_id: '',
          stripe_subscription_id: ''
        },
        {
          id: '547e51d6-6572-4474-936d-3f193f5f1095',
          username: 'casamensa',
          email: 'simmakin@aol.com',
          created_at: '2025-03-28T12:32:11.797132+00:00',
          role: 'user' as 'admin' | 'user',
          terms_accepted: false,
          terms_accepted_at: null,
          subscription_status: 'trial' as 'active' | 'trial' | 'past_due' | 'canceled' | 'unpaid',
          subscription_start_date: '2025-03-28T12:32:11.797132+00:00',
          subscription_end_date: '2025-04-28T12:32:11.797132+00:00',
          stripe_customer_id: '',
          stripe_subscription_id: ''
        }
      ];
    }
  },

  updateUserRole: async (userId: string, newRole: 'admin' | 'user') => {
    try {
      console.log(`updateUserRole: Setting user ${userId} role to "${newRole}"`);
      
      // Check for protected accounts
      const { data: userCheck, error: checkError } = await supabase
        .from('profiles')
        .select('email')
        .eq('id', userId)
        .single();
        
      if (checkError) {
        console.error('Error checking user email:', checkError);
      } else if (userCheck?.email === 'simmakin@gmail.com' && newRole !== 'admin') {
        console.log('Protected account detected, cannot change role from admin');
        return { success: false, error: 'This account must remain as admin' };
      }
      
      // Call the Edge Function
      try {
        console.log(`Invoking edge function update-user-role for user ${userId} to role ${newRole}`);
        const { data, error } = await supabase.functions.invoke(
          'update-user-role',
          {
            body: { userId, newRole }
          }
        );
        
        if (error) {
          console.error('Error in Edge Function call:', error);
          return { success: false, error };
        }
        
        console.log('Role update successful via Edge Function:', data);
        return { success: true, data };
      } catch (edgeError: any) {
        console.error('Error in Edge Function invocation:', edgeError);
        throw edgeError;
      }
    } catch (error: any) {
      console.error('Error in updateUserRole:', error);
      return { success: false, error };
    }
  },

  toggleUserAdminStatus: async (userId: string, isAdmin: boolean) => {
    try {
      const { data: currentUser, error: getUserError } = await supabase
        .from('profiles')
        .select('role, email, username')
        .eq('id', userId)
        .single();
      
      if (getUserError) {
        console.error('Error fetching current user data:', getUserError);
        throw getUserError;
      }
      
      console.log('Current user data before update:', currentUser);
      
      if (currentUser.email === 'simmakin@gmail.com' && !isAdmin) {
        console.log('Cannot demote special admin account (simmakin@gmail.com)');
        return currentUser;
      }
      
      const newRole = isAdmin ? 'admin' : 'user';
      console.log(`Updating user ${userId} role from ${currentUser?.role} to: ${newRole}`);
      
      const result = await supabaseService.updateUserRole(userId, newRole);
      
      if (!result.success) {
        // Type-safe error handling here
        const errorMsg = typeof result.error === 'string' 
          ? result.error 
          : result.error?.message || 'Unknown error';
        
        throw new Error(`Failed to update user role: ${errorMsg}`);
      }
      
      // After successful update, verify the change
      console.log(`Verifying role change for user ${userId}`);
      const { data: verifyUser, error: verifyError } = await supabase
        .from('profiles')
        .select('role, email, username')
        .eq('id', userId)
        .maybeSingle();
      
      if (verifyError) {
        console.error('Error verifying user update:', verifyError);
      } else {
        console.log('User data after update:', verifyUser);
      }
      
      return verifyUser || { id: userId, role: newRole, email: currentUser.email, username: currentUser.username };
    } catch (error) {
      console.error('Error updating user admin status:', error);
      throw error;
    }
  },

  refreshDatabaseReferences: async () => {
    try {
      console.log('Refreshing database schema references...');
      
      await supabase.from('profiles').select('count').limit(1);
      
      return true;
    } catch (error) {
      console.error('Error refreshing database references:', error);
      return false;
    }
  },

  synchronizeUsers: async () => {
    try {
      console.log('synchronizeUsers: Starting user synchronization...');
      
      await supabaseService.refreshDatabaseReferences();
      
      const { data: authData } = await supabase.auth.getUser();
      if (!authData?.user) {
        console.error('synchronizeUsers: No authenticated user to perform sync');
        return false;
      }
      
      // First, make sure the special admin account is always set correctly
      const { data: specialAdmin, error: specialAdminError } = await supabase
        .from('profiles')
        .select('*')
        .eq('email', 'simmakin@gmail.com')
        .maybeSingle();
        
      if (specialAdminError) {
        console.error('Error looking up special admin account:', specialAdminError);
      } else if (specialAdmin) {
        console.log('Special admin account found:', specialAdmin);
        
        if (specialAdmin.role !== 'admin') {
          console.log('Fixing role for special admin account');
          const { error: adminUpdateError } = await supabase
            .from('profiles')
            .update({ role: 'admin' })
            .eq('id', specialAdmin.id);
            
          if (adminUpdateError) {
            console.error('Error fixing special admin role:', adminUpdateError);
          } else {
            console.log('Successfully updated special admin role to admin');
          }
        }
      }
      
      const requiredUsers = [
        {
          id: 'fa0812e4-a398-4933-a2ab-4f859ceafeed',
          username: 'Sy',
          email: 'simmakin@gmail.com',
          role: 'admin' as 'admin' | 'user'
        },
        {
          id: '547e51d6-6572-4474-936d-3f193f5f1095',
          username: 'casamensa',
          email: 'simmakin@aol.com',
          role: 'user' as 'admin' | 'user'
        }
      ];
      
      for (const user of requiredUsers) {
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();
          
        if (error || !data) {
          console.log(`synchronizeUsers: User ${user.username} not found, inserting...`);
          
          const { error: insertError } = await supabase
            .from('profiles')
            .insert({
              id: user.id,
              username: user.username,
              email: user.email,
              role: user.role,
              subscription_status: 'trial',
              subscription_start_date: new Date().toISOString(),
              subscription_end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
            });
            
          if (insertError) {
            console.error(`Error inserting user ${user.username}:`, insertError);
          } else {
            console.log(`Successfully inserted user ${user.username} with role ${user.role}`);
          }
        } else {
          console.log(`synchronizeUsers: User ${user.username} found in profiles with role ${data.role}`);
          
          if (data.role !== user.role) {
            console.log(`synchronizeUsers: Updating role for ${user.username} from ${data.role} to ${user.role}`);
            
            const { error: updateError } = await supabase
              .from('profiles')
              .update({ role: user.role })
              .eq('id', user.id);
              
            if (updateError) {
              console.error(`Error updating role for user ${user.username}:`, updateError);
            } else {
              console.log(`Successfully updated role for user ${user.username}`);
            }
          }
        }
      }
      
      return true;
    } catch (error) {
      console.error('Error in synchronizeUsers:', error);
      return false;
    }
  },

  updateRoleWithRPC: async (userId: string, newRole: 'admin' | 'user') => {
    try {
      console.log(`Updating user ${userId} to role "${newRole}" using RPC`);
      
      const { data, error } = await supabase
        .from('profiles')
        .update({ role: newRole })
        .eq('id', userId)
        .select();
      
      if (error) {
        console.error('Error in updateRoleWithRPC:', error);
        return { success: false, error };
      }
      
      return { success: true, data };
    } catch (error) {
      console.error('Error in updateRoleWithRPC:', error);
      return { success: false, error };
    }
  },

  directSqlUpdate: async (userId: string, newRole: 'admin' | 'user') => {
    try {
      console.log(`Updating user ${userId} to role "${newRole}" using direct SQL`);
      
      const { data, error } = await supabase
        .from('profiles')
        .update({ role: newRole })
        .eq('id', userId)
        .select();
      
      if (error) {
        console.error('Error in directSqlUpdate:', error);
        return { success: false, error };
      }
      
      return { success: true, data };
    } catch (error) {
      console.error('Error in directSqlUpdate:', error);
      return { success: false, error };
    }
  },

  recreateProfileWithRole: async (userId: string, newRole: 'admin' | 'user') => {
    try {
      console.log(`Recreating profile for user ${userId} with role "${newRole}"`);
      
      const { data: currentProfile, error: fetchError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();
      
      if (fetchError) {
        console.error('Error fetching current profile:', fetchError);
        return { success: false, error: fetchError };
      }
      
      const { error: deleteError } = await supabase
        .from('profiles')
        .delete()
        .eq('id', userId);
      
      if (deleteError) {
        console.error('Error deleting profile:', deleteError);
        return { success: false, error: deleteError };
      }
      
      const { data: newProfile, error: insertError } = await supabase
        .from('profiles')
        .insert({
          ...currentProfile,
          id: userId,
          role: newRole
        })
        .select();
      
      if (insertError) {
        console.error('Error creating new profile:', insertError);
        return { success: false, error: insertError };
      }
      
      return { success: true, data: newProfile };
    } catch (error) {
      console.error('Error in recreateProfileWithRole:', error);
      return { success: false, error };
    }
  },

  rawEnumUpdate: async (userId: string, newRole: 'admin' | 'user') => {
    try {
      console.log(`Updating user ${userId} to role "${newRole}" using enum-aware update`);
      
      const { data, error } = await supabase
        .from('profiles')
        .update({ role: newRole })
        .eq('id', userId)
        .select();
      
      if (error) {
        console.error('Error in rawEnumUpdate:', error);
        return { success: false, error };
      }
      
      return { success: true, data };
    } catch (error) {
      console.error('Error in rawEnumUpdate:', error);
      return { success: false, error };
    }
  },

  postgresDirectUpdate: async (userId: string, newRole: 'admin' | 'user') => {
    try {
      console.log(`Updating user ${userId} to role "${newRole}" using PostgreSQL-specific update`);
      
      const { data, error } = await supabase
        .from('profiles')
        .update({ role: newRole })
        .eq('id', userId)
        .select();
      
      if (error) {
        console.error('Error in postgresDirectUpdate:', error);
        return { success: false, error };
      }
      
      return { success: true, data };
    } catch (error) {
      console.error('Error in postgresDirectUpdate:', error);
      return { success: false, error };
    }
  },

  directDatabaseUpdate: async (userId: string, newRole: 'admin' | 'user') => {
    try {
      console.log(`Emergency update for user ${userId} to role "${newRole}"`);
      
      const { data, error } = await supabase
        .from('profiles')
        .update({ role: newRole })
        .eq('id', userId)
        .select();
      
      if (error) {
        console.error('Error in directDatabaseUpdate:', error);
        return { success: false, error };
      }
      
      return { success: true, data };
    } catch (error) {
      console.error('Error in directDatabaseUpdate:', error);
      return { success: false, error };
    }
  },

  executeRawSql: async (userId: string, newRole: 'admin' | 'user') => {
    try {
      console.log(`Executing raw SQL to update user ${userId} to role "${newRole}"`);
      
      const { data, error } = await supabase
        .from('profiles')
        .update({ role: newRole })
        .eq('id', userId)
        .select();
      
      if (error) {
        console.error('Error in executeRawSql:', error);
        return { success: false, error };
      }
      
      return { success: true, data };
    } catch (error) {
      console.error('Error in executeRawSql:', error);
      return { success: false, error };
    }
  },

  // Invitation Management
  createInvitation: async (email: string, role: 'user' | 'admin' = 'user', isFreeAccount: boolean = true, expiryDate?: string) => {
    try {
      // Get current session and ensure we're authenticated
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        console.error('No active session when trying to create invitation');
        return { 
          success: false, 
          error: new Error('Authentication required')
        };
      }
      
      // Manually set the auth token for this specific function call
      const functionOptions = {
        headers: {
          Authorization: `Bearer ${sessionData.session.access_token}`
        },
        body: { 
          email, 
          role, 
          isFreeAccount, 
          expiryDate 
        }
      };
      
      const { data, error } = await supabase.functions.invoke(
        'create-invitation',
        functionOptions
      );
      
      if (error) {
        console.error('Error creating invitation:', error);
        return { success: false, error };
      }
      
      return { success: true, data };
    } catch (error) {
      console.error('Error in createInvitation:', error);
      return { success: false, error };
    }
  },

  listInvitations: async (status?: 'pending' | 'accepted' | 'expired', limit = 20, offset = 0) => {
    try {
      // Build URL with query parameters 
      let url = 'list-invitations';
      const queryParams = [];
      if (status) queryParams.push(`status=${status}`);
      if (limit) queryParams.push(`limit=${limit}`);
      if (offset) queryParams.push(`offset=${offset}`);
      
      if (queryParams.length > 0) {
        url += `?${queryParams.join('&')}`;
      }
      
      console.log(`Invoking list-invitations with URL: ${url}`);
      
      // Get current session and ensure we're authenticated
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        console.error('No active session when trying to list invitations');
        return { 
          success: false, 
          error: new Error('Authentication required')
        };
      }
      
      // Log auth info for debugging
      console.log('Auth session exists:', !!sessionData.session);
      console.log('Auth token available:', !!sessionData.session?.access_token);
      
      // Call the function with proper auth using GET
      const result = await supabase.functions.invoke(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${sessionData.session.access_token}`,
        }
      });
      
      if (result.error) {
        console.error('Error listing invitations:', result.error);
        return { success: false, error: result.error };
      }
      
      if (!result.data) {
        console.error('No data returned from list-invitations function');
        return { 
          success: false, 
          error: new Error('No data returned from server')
        };
      }
      
      console.log('Invitations data received:', result.data);
      
      return { 
        success: true, 
        invitations: result.data.data, 
        pagination: result.data.pagination 
      };
    } catch (error) {
      console.error('Error in listInvitations:', error);
      return { success: false, error };
    }
  },

  updateInvitation: async (invitationId: string, updateData: { 
    email?: string, 
    role?: 'user' | 'admin', 
    isFreeAccount?: boolean, 
    expiryDate?: string | null 
  }) => {
    try {
      const { data, error } = await supabase.functions.invoke(
        'manage-invitation',
        {
          body: { 
            action: 'update',
            invitationId,
            data: updateData
          }
        }
      );
      
      if (error) {
        console.error('Error updating invitation:', error);
        return { success: false, error };
      }
      
      return { success: true, data };
    } catch (error) {
      console.error('Error in updateInvitation:', error);
      return { success: false, error };
    }
  },

  deleteInvitation: async (invitationId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke(
        'manage-invitation',
        {
          body: { 
            action: 'delete',
            invitationId
          }
        }
      );
      
      if (error) {
        console.error('Error deleting invitation:', error);
        return { success: false, error };
      }
      
      return { success: true };
    } catch (error) {
      console.error('Error in deleteInvitation:', error);
      return { success: false, error };
    }
  },

  resetToNormalAccount: async (invitationId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke(
        'manage-invitation',
        {
          body: { 
            action: 'reset',
            invitationId
          }
        }
      );
      
      if (error) {
        console.error('Error resetting account:', error);
        return { success: false, error };
      }
      
      return { success: true };
    } catch (error) {
      console.error('Error in resetToNormalAccount:', error);
      return { success: false, error };
    }
  },

  getCurrentUser: async () => {
    try {
      const { data, error } = await supabase.auth.getSession();
      
      if (error || !data.session) {
        return null;
      }
      
      return data.session.user as unknown as User;
    } catch (error) {
      console.error('Get current user error:', error);
      return null;
    }
  },

  getProfile: async () => {
    try {
      // First get the current user ID
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !sessionData.session) {
        return { data: null, error: sessionError || new Error('No active session') };
      }
      
      const userId = sessionData.session.user.id;
      
      // Then fetch profile data
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();
      
      if (error) {
        console.error('Get profile error:', error);
        return { data: null, error };
      }
      
      return { data, error: null };
    } catch (error) {
      console.error('Get profile error:', error);
      return { data: null, error };
    }
  },

  checkSessionsTable: async () => {
    try {
      // This makes a direct query to check if the sessions table has any data
      console.log('Checking if sessions table has any records');
      const { count, error } = await supabase
        .from('sessions')
        .select('*', { count: 'exact', head: true });
      
      console.log('Sessions table count:', count);
      
      if (error) {
        console.error('Error checking sessions table:', error);
        return { exists: false, count: 0, error };
      }
      
      return { exists: true, count: count || 0 };
    } catch (error) {
      console.error('Error in checkSessionsTable:', error);
      return { exists: false, count: 0, error };
    }
  },

  // GDPR: Right to be forgotten - delete user profile and cascade to all user data
  // This function should ensure all user data (clients, sessions, supervision, CPD, etc.) is deleted when a profile is deleted.
  deleteUser: async (userId: string) => {
    try {
      // Implement the logic to delete all user data associated with the given userId
      // This might involve calling multiple delete functions and ensuring all data is deleted
      await supabaseService.deleteAllUserClients();
      await supabaseService.deleteAllUserSessions();
      
      // Then delete the user profile
      const { error: deleteProfileError } = await supabase
        .from('profiles')
        .delete()
        .eq('id', userId);
      
      if (deleteProfileError) {
        console.error('Error deleting user profile:', deleteProfileError);
        throw deleteProfileError;
      }
      
      return true;
    } catch (error) {
      console.error('Error in deleteUser:', error);
      throw error;
    }
  },
};
